import { useEffect, useRef, useState } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · ADAPTIVE FILTERS — a dependent filter offers only what the   │
 * │ chosen project (or projects, their union) has.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: with a project chosen, the contract, line item, task and tracker filters
 * still offered the whole workspace, so most choices answered *"nothing"*. Each list's dependent
 * options are re-read when the selection they depend on changes — `loader` asks the server for
 * that selection, `key` is what it depends on, as one string (the selection's own comma list, or
 * several joined). A string rather than a dependency array, so the effect's dependencies are a
 * literal the hooks lint can check.
 *
 * ⚠️⚠️ **`null` MEANS STILL LOADING, AND A CALLER MUST NOT PRUNE ON IT.** A remembered value or one
 * the person just chose would be dropped in the moment between the change and the answer. The
 * previous answer is NOT kept while the next loads: options for the old selection are exactly
 * what must not be offered for the new one.
 *
 * ⚠️ **A late answer for an old selection is dropped**, the `alive` pattern `tracker-filters.ts`
 * uses. A failed load stays `null`, so an outage prunes nothing.
 */
export function useFilterOptions<T>(loader: () => Promise<T | null>, key: string): T | null {
  const [options, setOptions] = useState<T | null>(null);
  const latest = useRef(loader);
  latest.current = loader;
  useEffect(() => {
    const alive = { current: true };
    setOptions(null);
    void latest
      .current()
      .catch(() => null)
      .then((value) => {
        if (alive.current && value !== null) setOptions(value);
      });
    return () => {
      alive.current = false;
    };
  }, [key]);
  return options;
}
